import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Instagram, ShoppingBag, ArrowRight, Star, Quote } from "lucide-react";
import { useSeo, useJsonLd, SITE_URL } from "@/lib/seo";
import { INSTAGRAM_URL, INSTAGRAM_HANDLE } from "@/lib/social";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FollowButton = ({ testId, children, variant = "primary" }) => {
  const disabled = !INSTAGRAM_URL;
  const base =
    "inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
  const styles =
    variant === "primary"
      ? "bg-[var(--oz-dark)] text-white hover:bg-black"
      : "border-2 border-[var(--oz-dark)] text-[var(--oz-dark)] hover:bg-[var(--oz-dark)] hover:text-white";
  if (disabled) {
    return (
      <button data-testid={testId} disabled className={`${base} ${styles}`} title="Compte bientôt disponible">
        <Instagram className="w-4 h-4" /> Bientôt disponible
      </button>
    );
  }
  return (
    <a
      data-testid={testId}
      href={INSTAGRAM_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`${base} ${styles}`}
    >
      <Instagram className="w-4 h-4" /> {children}
    </a>
  );
};

export default function InstagramPage() {
  const [posts, setPosts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();

  const goToPacks = () => {
    navigate("/packs");
    setTimeout(() => document.getElementById("packs")?.scrollIntoView({ behavior: "smooth" }), 120);
  };

  useSeo({
    title: "Instagram Ozentya — Packs Pro à revendre | Suivez-nous",
    description:
      "Suivez le compte Instagram des Packs Pro Ozentya : inspirations, nouveautés et conseils pour développer votre activité de revente de produits beauté.",
    path: "/instagram",
  });
  useJsonLd("ld-instagram", {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    name: "Instagram Ozentya — Packs Pro",
    url: `${SITE_URL}/instagram`,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/instagram`).then(({ data }) => setPosts(data)).catch(() => {});
    axios.get(`${API}/reviews`).then(({ data }) => setReviews(data.slice(0, 3))).catch(() => {});
  }, []);

  const accountName = `@${INSTAGRAM_HANDLE}`;

  return (
    <div data-testid="instagram-page">
      {/* En-tête */}
      <section className="px-4 sm:px-8 lg:px-16 max-w-5xl mx-auto pt-14 md:pt-20 pb-10 text-center">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--oz-gold)] mb-4">
          <Instagram className="w-4 h-4" /> Ozentya sur Instagram
        </span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[var(--oz-text)]">
          Instagram Ozentya
        </h1>
        <p className="mt-5 text-base text-[var(--oz-muted)] leading-relaxed max-w-2xl mx-auto">
          Suivez le compte dédié aux <strong>Packs Pro Ozentya</strong> : inspirations, coulisses,
          nouveautés et conseils pour lancer et développer votre activité de revente de produits
          beauté, à votre rythme.
        </p>
      </section>

      {/* Bloc Instagram principal */}
      <section className="px-4 sm:px-8 lg:px-16 max-w-5xl mx-auto pb-14">
        <div className="relative overflow-hidden rounded-[1.75rem] bg-[var(--oz-dark)] text-white px-6 sm:px-12 py-12 text-center">
          <div className="mx-auto w-28 h-28 rounded-full p-[3px] bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] mb-5">
            <img
              data-testid="instagram-profile-photo"
              src="/img/ozentya-instagram-profile.jpg"
              alt="Profil Instagram Ozentya Packs Pro"
              className="w-full h-full rounded-full object-cover border-4 border-[var(--oz-dark)]"
            />
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl text-white">{accountName}</h2>
          <p className="mt-3 text-sm text-white/70 leading-relaxed max-w-xl mx-auto">
            Rejoignez la communauté des revendeuses Ozentya et ne manquez aucune nouveauté sur nos
            Packs Pro.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <FollowButton testId="instagram-follow-main">Suivre sur Instagram</FollowButton>
            <button
              data-testid="instagram-order-pack-main"
              onClick={goToPacks}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm tracking-wide bg-[var(--oz-gold)] text-[var(--oz-dark)] hover:brightness-95 transition-all"
            >
              <ShoppingBag className="w-4 h-4" /> Commander un pack
            </button>
          </div>
        </div>
      </section>

      {/* Nos clientes témoignent */}
      {reviews.length > 0 && (
        <section data-testid="instagram-testimonials" className="px-4 sm:px-8 lg:px-16 max-w-6xl mx-auto pb-14">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--oz-gold)] mb-3">
              <Star className="w-4 h-4 fill-[var(--oz-gold)]" /> Nos clientes témoignent
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl text-[var(--oz-text)]">
              Elles nous font confiance
            </h2>
          </div>
          <div className={`grid gap-4 sm:gap-6 ${reviews.length === 1 ? "grid-cols-1 max-w-xl mx-auto" : reviews.length === 2 ? "grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto" : "grid-cols-1 md:grid-cols-3"}`}>
            {reviews.map((r, i) => (
              <div
                key={i}
                data-testid={`instagram-testimonial-${i}`}
                className="relative rounded-2xl bg-white border border-[var(--oz-border)] p-6 shadow-[0_8px_30px_-18px_rgba(0,0,0,0.25)]"
              >
                <Quote className="w-7 h-7 text-[var(--oz-gold)]/40 mb-2" />
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star
                      key={s}
                      className={`w-4 h-4 ${s < (r.rating || 5) ? "fill-[var(--oz-gold)] text-[var(--oz-gold)]" : "text-[var(--oz-border)]"}`}
                    />
                  ))}
                </div>
                <p className="text-sm text-[var(--oz-text)] leading-relaxed italic">“{r.message}”</p>
                <p className="mt-4 text-sm font-semibold text-[var(--oz-text)]">{r.name}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Galerie */}
      <section className="bg-[var(--oz-bg-2)]">
        <div className="px-4 sm:px-8 lg:px-16 max-w-6xl mx-auto py-14 md:py-20">
          <h2 className="font-serif text-2xl sm:text-3xl text-[var(--oz-text)] text-center mb-3">
            Nos publications
          </h2>
          <p className="text-sm text-[var(--oz-muted)] text-center max-w-xl mx-auto mb-10">
            Un aperçu de notre univers. Cliquez sur une publication pour la découvrir sur Instagram.
          </p>

          {posts.length === 0 ? (
            <p data-testid="instagram-empty" className="text-center text-[var(--oz-muted)] py-10">
              Les publications arrivent très bientôt. ✨
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {posts.map((p, i) => {
                const href = p.url || INSTAGRAM_URL || null;
                const Wrapper = href ? "a" : "div";
                const wrapperProps = href
                  ? { href, target: "_blank", rel: "noopener noreferrer" }
                  : {};
                return (
                  <Wrapper
                    key={p.id}
                    data-testid={`instagram-post-${i}`}
                    {...wrapperProps}
                    className="group relative block aspect-square overflow-hidden rounded-2xl bg-white border border-[var(--oz-border)]"
                  >
                    <img
                      src={p.image}
                      alt={p.caption || `Publication Instagram Ozentya ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                      <span className="text-white text-xs leading-snug">{p.caption}</span>
                    </div>
                    <span className="absolute top-2.5 right-2.5 text-white/90 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Instagram className="w-4 h-4" />
                    </span>
                  </Wrapper>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Appel à l'action */}
      <section className="px-4 sm:px-8 lg:px-16 max-w-4xl mx-auto py-16 md:py-24 text-center">
        <h2 className="font-serif text-3xl sm:text-4xl font-medium tracking-tight text-[var(--oz-text)]">
          Retrouvez-nous sur Instagram
        </h2>
        <p className="mt-4 text-base text-[var(--oz-muted)] max-w-xl mx-auto">
          Découvrez nos packs, nos nouveautés et l'aventure de nos revendeuses. On vous attend !
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            data-testid="instagram-order-pack-cta"
            onClick={goToPacks}
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm tracking-wide bg-[var(--oz-gold)] text-[var(--oz-dark)] hover:brightness-95 transition-all"
          >
            <ShoppingBag className="w-4 h-4" /> Commander un pack <ArrowRight className="w-4 h-4" />
          </button>
          <FollowButton testId="instagram-cta-button" variant="secondary">Voir notre Instagram</FollowButton>
        </div>
      </section>
    </div>
  );
}
